import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Loader2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SERVICE_OPTIONS } from "@/content/site";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;
const EMPTY = { name: "", city: "", service: "", rating: 0, text: "", website: "" };
const inputCls = "w-full rounded-xl border border-white/12 bg-white/[0.03] px-4 py-3 text-[15px] text-white placeholder:text-zinc-500 outline-none transition-[border-color] duration-300 focus:border-amber-400/70";

const Stars = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1.5" role="radiogroup" aria-label="Star rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" role="radio" aria-checked={value === n} aria-label={`${n} star${n > 1 ? "s" : ""}`}
          onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => onChange(n)} data-testid={`review-star-${n}`}
          className="transition-transform duration-200 hover:scale-110">
          <Star className={`h-8 w-8 ${(hover || value) >= n ? "fill-amber-400 text-amber-400" : "text-zinc-600"}`} />
        </button>
      ))}
    </div>
  );
};

export const ReviewForm = ({ open, onOpenChange }) => {
  const [f, setF] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const close = (v) => { onOpenChange(v); if (!v) setTimeout(() => setDone(false), 300); };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.rating) return toast.error("Please choose a star rating.");
    if (f.name.trim().length < 2) return toast.error("Please enter your name.");
    if (!f.service) return toast.error("Please select the service you received.");
    if (f.text.trim().length < 20) return toast.error("Please write at least a sentence or two (20+ characters).");
    if (!API) return toast.error("Reviews are unavailable right now.");
    setSending(true);
    try {
      await axios.post(`${API}/reviews`, f);
      setDone(true);
      setF(EMPTY);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.[0]?.msg || "Something went wrong. Please try again.");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="review-dialog" data-lenis-prevent className="max-h-[90vh] max-w-lg overflow-y-auto border-white/10 bg-[#0d0f12] p-6 sm:p-8 text-white">
        {done ? (
          <div className="py-8 text-center" data-testid="review-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-amber-400" />
            <DialogTitle className="mt-5 text-xl font-semibold uppercase">Thank you!</DialogTitle>
            <DialogDescription className="mt-3 text-zinc-300">Your review was submitted and will appear on the site once it's approved.</DialogDescription>
          </div>
        ) : (
          <form onSubmit={submit} noValidate data-testid="review-form">
            <DialogTitle className="text-xl font-semibold uppercase">Leave a Review</DialogTitle>
            <DialogDescription className="mt-2 text-sm text-zinc-400">Tell other Texas homeowners about your experience with Solar Energy Doc.</DialogDescription>
            <div className="mt-6"><Stars value={f.rating} onChange={set("rating")} /></div>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <input value={f.name} onChange={set("name")} placeholder="Name (e.g. Mark R.)" maxLength={60} className={inputCls} data-testid="review-name-input" aria-label="Name" />
              <input value={f.city} onChange={set("city")} placeholder="City" maxLength={60} className={inputCls} data-testid="review-city-input" aria-label="City" />
            </div>
            <div className="mt-4">
              <Select value={f.service} onValueChange={set("service")}>
                <SelectTrigger data-testid="review-service-select" className="h-auto rounded-xl border-white/12 bg-white/[0.03] px-4 py-3 text-[15px] text-white"><SelectValue placeholder="Service you received" /></SelectTrigger>
                <SelectContent data-lenis-prevent className="z-[60] border-white/10 bg-[#14171c] text-white">
                  {SERVICE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <textarea value={f.text} onChange={set("text")} rows={5} maxLength={1200} placeholder="How was your experience?" className={`${inputCls} mt-4 resize-none`} data-testid="review-text-input" aria-label="Review" />
            <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
            <button type="submit" disabled={sending} data-testid="review-submit-button" className="btn btn-primary mt-6 w-full disabled:opacity-70">
              {sending && <Loader2 className="relative z-10 h-4 w-4 animate-spin" />}
              <span className="relative z-10">{sending ? "Submitting…" : "Submit Review"}</span>
            </button>
            <p className="mt-4 text-xs text-zinc-400">Reviews are checked before they're published.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
